import Div from 'components/Div';
import FormInput from 'components/FormInput/index';
import ElectricityIcon from '@enerex/unified-ui-react/icons/ElectricityIcon';
import GasIcon from '@enerex/unified-ui-react/icons/GasIcon';
import CloseIcon from '@enerex/unified-ui-react/icons/CloseIcon';
import ConfirmationModal from '@enerex/unified-ui-react/ConfirmationModal';
import { PricingState } from 'pages/rankings/store/state';
import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { Form, Button, Badge, Dropdown } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { setSelectedMarket } from 'pages/rankings/store/actions';
import { useWatch } from 'react-hook-form';

const EXCLUSIVE_PASS_THROUGH_VALUES = new Set(["All", "All Inclusive"]);
const BOOLEAN_FILTER_OPTIONS = [
  { id: 'All', value: 'All', name: 'All' },
  { id: 'Yes', value: 'Yes', name: 'Yes' },
  { id: 'No', value: 'No', name: 'No' },
];

type DropdownLoadingMap = {
  states?: boolean;
  utilities?: boolean;
  rateClasses?: boolean;
  loadProfiles?: boolean;
  loadZones?: boolean;
  billingMethods?: boolean;
  adminClients?: boolean;
  costComponents?: boolean;
  customerTypes?: boolean;
};

const AccordionFilters = ({ hide, handleSubmit, register, errors, control, reset, dependantDropdowns, onDeleteFilter, resetKey, adminClients, trigger, dropdownLoading, costComponentWarning, setValue, filterActionsDisabled, savedFilters, onSaveFilters, onDeleteSavedFilter, onApplySavedFilter, savingFilters, selectedSavedFilterId }: { hide?: () => void; handleSubmit: any; register?: any; errors?: any; control?: any; reset?: any; dependantDropdowns: any; onDeleteFilter?: any; resetKey?: number; adminClients?: any[]; trigger?: any; dropdownLoading?: DropdownLoadingMap; costComponentWarning?: boolean; setValue?: any; filterActionsDisabled?: boolean; savedFilters?: Array<{ id: string; name: string }>; onSaveFilters?: (name: string) => void; onDeleteSavedFilter?: (filterId: string) => void; onApplySavedFilter?: (filterId: string) => void; savingFilters?: boolean; selectedSavedFilterId?: string | null; }) => {
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [filterName, setFilterName] = useState("");

  const pricingState = useSelector((state: { ranking: PricingState }) => state.ranking);

  const termsList = pricingState.terms;
  const markets = pricingState.markets;
  const selectedMarket = pricingState.selectedMarket;
  const productTypeList = pricingState.productTypes;
  const costComponentList = pricingState.costComponents;
  const filteredCostComponents =
    costComponentList?.filter((item: any) => item.value !== "All") || [];
  const watchedStartDate = useWatch({ control, name: 'startToDate' });
    
//   [
//   { value: 'Fixed', name: 'Fixed' },
//   { value: 'Index', name: 'Index' },
//   { value: 'Hybrid', name: 'Hybrid' },
// ];
  // pricingState.productTypes;
  // console.log("pricingState.productTypes",pricingState.productTypes);
  // console.log("pricingState.terms.Rankings",pricingState.terms);
  // console.log("pricingState.productTypes.Rankings",pricingState.productTypes);
  const dispatch = useDispatch();
  const selectedState = pricingState.selectedState?.name;
  const selectedTerm = pricingState.terms.find((s: any) => s.isSelected)?.name || 'All';
  const selectedUtility = pricingState?.selectedUtility?.name;
  const selectedRateClass = pricingState?.selectedRateClass?.name;
  const selectedLoadProfile = pricingState?.selectedLoadProfile?.name;
  const selectedLoadZone = pricingState?.selectedLoadZone?.name;
  const selectedBillingMethod = pricingState?.selectedBillingMethod?.name;
  const selectedCustomerClass =
    pricingState.customerClass === "COMM"
      ? "Commercial"
      : pricingState.customerClass === "RESI"
      ? "Residential"
      : pricingState.customerClass === "NONRESI"
      ? "Non Residential"
      : "All";
  const selectedCommodity = pricingState.commodity === 2 ? 'Nat Gas' : 'Electric';
  const usage = pricingState.usage;
  const demand = pricingState.demand;
  const loadFactor = pricingState.loadFactor;
  // const selectedProductType = pricingState?.productTypes.find((s: any) => s.isSelected)?.name || '';
  //   console.log("selectedProductTypeyyyyyyyyyyyyyyyyyyyyyyy",selectedProductType);
  const selectedProductType = pricingState?.productTypes
    .filter((s: any) => s.isSelected)
    .map((s: any) => s.value)
    .join(', ') || '';

  const selectedCostComponent = pricingState?.costComponents
    .filter((s: any) => s.isSelected)
    .map((s: any) => s.value)
    .join(', ') || '';
  
  const dropdownLoadingState: DropdownLoadingMap = dropdownLoading || {};

  const passThroughSelection = useWatch({ control, name: 'passThrough' });
  const watchedEffectiveDate = useWatch({ control, name: 'effectiveDate' });
  const { exclusivePassThroughValue, hasNonExclusiveSelection } = useMemo(() => {
    const selections = Array.isArray(passThroughSelection)
      ? passThroughSelection
      : passThroughSelection
      ? [passThroughSelection]
      : [];
    const exclusiveSelection = selections.find((item: any) => {
      const value = item?.value ?? item;
      return (
        typeof value === 'string' && EXCLUSIVE_PASS_THROUGH_VALUES.has(value)
      );
    });
    const exclusiveValue =
      exclusiveSelection?.value ?? exclusiveSelection ?? null;
    const nonExclusiveSelected = selections.some((item: any) => {
      const value = item?.value ?? item;
      return (
        value &&
        typeof value === 'string' &&
        !EXCLUSIVE_PASS_THROUGH_VALUES.has(value)
      );
    });
    return {
      exclusivePassThroughValue: exclusiveValue,
      hasNonExclusiveSelection: nonExclusiveSelected,
    };
  }, [passThroughSelection]);

  // When "All" is selected as Pass Through default, hide it from the options list
  // When other options are selected, "All" should remain available in options
  const baseCostComponents = useMemo(() => {
    const filtered =
      costComponentList?.filter((item: any) => item.value !== "All") || [];
    const allInclusiveOption = filtered.find(
      (item: any) => item.value === "All Inclusive"
    );
    const remainingOptions = filtered
      .filter((item: any) => item.value !== "All Inclusive")
      .sort((a: any, b: any) =>
        String(a?.name || a?.value || "").localeCompare(
          String(b?.name || b?.value || ""),
          undefined,
          { sensitivity: "base" }
        )
      );

    return allInclusiveOption
      ? [allInclusiveOption, ...remainingOptions]
      : remainingOptions;
  }, [costComponentList]);

  const passThroughOptions = useMemo(() => {
    const selections = Array.isArray(passThroughSelection)
      ? passThroughSelection
      : passThroughSelection
      ? [passThroughSelection]
      : [];
    const isAllSelected = selections.some((item: any) => {
      const value = item?.value ?? item;
      return value === "All";
    });

    // If "All" is selected, hide it from options
    if (isAllSelected) {
      return baseCostComponents.filter((item: any) => item.value !== "All");
    }

    // If "All" is not selected, add it back to the options list
    const allOption = costComponentList?.find((item: any) => item.value === "All");
    if (allOption) {
      return [allOption, ...baseCostComponents];
    }

    return baseCostComponents;
  }, [baseCostComponents, passThroughSelection, costComponentList]);

  // Auto-filter passThrough selections based on exclusive rules
  useEffect(() => {
    if (!setValue || !passThroughSelection) return;
    
    const selections = Array.isArray(passThroughSelection)
      ? passThroughSelection
      : passThroughSelection
      ? [passThroughSelection]
      : [];
    
    if (selections.length === 0) return;

    // Extract values from selections
    const getValue = (item: any) => item?.value ?? item;
    const values = selections.map(getValue);

    // Check what's selected
    const hasAll = values.includes("All");
    const hasAllInclusive = values.includes("All Inclusive");
    const hasExclusive = hasAll || hasAllInclusive;
    const hasNonExclusive = values.some((v: any) => v && typeof v === "string" && !EXCLUSIVE_PASS_THROUGH_VALUES.has(v));

    let filteredSelections: any[] = [];

    // Rule 3: If both "All" and "All Inclusive" are selected, keep only the last one
    if (hasAll && hasAllInclusive) {
      // Keep only the last selected exclusive value (the one that appears last in the array)
      const lastExclusive = selections.filter((item: any) => {
        const value = getValue(item);
        return EXCLUSIVE_PASS_THROUGH_VALUES.has(value);
      }).pop();
      filteredSelections = lastExclusive ? [lastExclusive] : [];
    }
    // Rule 1 & 2: When both exclusive and non-exclusive are present, check the last selected item
    else if (hasNonExclusive && hasExclusive) {
      // Get the last selected item to determine which rule to apply
      const lastSelected = selections[selections.length - 1];
      const lastSelectedValue = getValue(lastSelected);
      const isLastExclusive = EXCLUSIVE_PASS_THROUGH_VALUES.has(lastSelectedValue);
      
      if (isLastExclusive) {
        // Rule 2: Last selected is exclusive, remove non-exclusive options
        filteredSelections = selections.filter((item: any) => {
          const value = getValue(item);
          return EXCLUSIVE_PASS_THROUGH_VALUES.has(value);
        });
      } else {
        // Rule 1: Last selected is non-exclusive, remove exclusive options
        filteredSelections = selections.filter((item: any) => {
          const value = getValue(item);
          return value && typeof value === "string" && !EXCLUSIVE_PASS_THROUGH_VALUES.has(value);
        });
      }
    }
    // No violations, keep selections as is
    else {
      filteredSelections = selections;
    }

    // Only update if filtered selection is different from current
    const currentValues = values.sort().join(',');
    const filteredValues = filteredSelections.map(getValue).sort().join(',');
    
    if (currentValues !== filteredValues) {
      // Use setTimeout to avoid conflict with Controller's onChange
      setTimeout(() => {
        setValue('passThrough', filteredSelections.length > 0 ? filteredSelections : [], { shouldDirty: true });
      }, 0);
    }
  }, [passThroughSelection, setValue]);

  const toShortDate = (d?: string | Date | null) => {
    if (!d) return null;
    
    // If it's a string in YYYY-MM-DD format, parse it as local date to avoid timezone shifts
    let dt: Date;
    if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
      const [year, month, day] = d.split('-').map(Number);
      dt = new Date(year, month - 1, day); // month is 0-indexed
    } else {
      dt = new Date(d);
    }
    
    if (isNaN(dt.getTime())) return null;
    
    // Format as mm/dd/yy
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    const yy = String(dt.getFullYear()).slice(-2);
    return `${mm}/${dd}/${yy}`;
  };

  const toMonthYearDate = (d?: string | Date | any | null) => {
    if (!d) return null;

    let dt: Date | null = null;

    if (d && typeof d === 'object' && typeof d.toDate === 'function') {
      dt = d.toDate();
    } else if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
      // If it's a string in YYYY-MM-DD format, parse it as local date to avoid timezone shifts
      const [year, month, day] = d.split('-').map(Number);
      dt = new Date(year, month - 1, day); // month is 0-indexed
    } else {
      dt = new Date(d);
    }

    if (!dt || isNaN(dt.getTime())) return null;

    // Format as mm/yy
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const yy = String(dt.getFullYear()).slice(-2);
    return `${mm}/${yy}`;
  };

  // Check if current market is gas (handle various commodity naming conventions)
  const commodityStr = selectedMarket?.commodity ? String(selectedMarket.commodity).toLowerCase() : '';
  const isGasMarket = commodityStr.includes('gas') || commodityStr.includes('natgas');
  const demandLabel = isGasMarket ? "Demand (therm)" : "Demand (kW)";
  const usageLabel = isGasMarket ? "Usage (therm)" : "Usage (kWh)";

  // Function to get next month's date in mm/yy format
  const getNextMonthDate = () => {
    const date = new Date();
    date.setDate(1); // Set to first day
    date.setMonth(date.getMonth() + 1); // Move to next month
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yy = String(date.getFullYear()).slice(-2);
    return `${mm}/${yy}`;
  };

  // const formattedStartDate = getNextMonthDate();
  const startDateFallback =
    pricingState.startToDate || pricingState.startFromDate || watchedStartDate;
  const formattedStartDate = startDateFallback
    ? toMonthYearDate(startDateFallback)
    : null;

  const selectedFilters: Record<string, string | null> = {
    Market: selectedMarket?.name || 'All',
    State: selectedState || 'All',
    Utility: selectedUtility || 'All',
    // Show Load Zone only for non-gas markets when state is Texas
    'Load Zone': !isGasMarket ? (selectedLoadZone || 'All') : null,
    'Rate Class': pricingState.selectedState?.name === 'Texas' ? (selectedLoadProfile || 'All') : (selectedRateClass || 'All'),
    'Customer Class': selectedCustomerClass || 'All',
    'Billing Method': selectedBillingMethod || 'All',
    'Pass Through': selectedCostComponent || '',
    'Usage': (usage !== undefined && usage !== null) ? String(usage) : null,
    'Demand': (demand !== undefined && demand !== null) ? String(demand) : null,
    'Load Factor': (loadFactor !== undefined && loadFactor !== null) ? String(loadFactor) : null,
    Term: selectedTerm || 'All',
    ProductType: selectedProductType || '',
    'Start Date': formattedStartDate || 'All',

    // 'Effective Date': (() => {
    //   if (pricingState.effectiveToDate) {
    //     return toShortDate(pricingState.effectiveToDate);
    //   }
    //   if (pricingState.effectiveFromDate) {
    //     return toShortDate(pricingState.effectiveFromDate);
    //   }
    //   if (Array.isArray(watchedEffectiveDate) && watchedEffectiveDate.length >= 2) {
    //     return `${toShortDate(watchedEffectiveDate[0])} - ${toShortDate(
    //       watchedEffectiveDate[1]
    //     )}`;
    //   }
    //   if (watchedEffectiveDate) {
    //     return toShortDate(watchedEffectiveDate);
    //   }
    //   return null;
    // })() || 'All',
  };

  const getBadgesForSection = (sectionKey: string) => {
      const rateClassLabel = pricingState.selectedState?.name === 'Texas' ? 'Load Profile' : 'Rate Class';

    const mapping: Record<string, Array<[string, string | null]>> = {
      Market: [["Market", selectedFilters["Market"]]],
      Geography: [
        ["State", selectedFilters["State"]],
        ["Utility", selectedFilters["Utility"]],
        // Only include Load Zone badge for non-gas markets when state is Texas
        ...(!isGasMarket
          ? [["Load Zone", selectedFilters["Load Zone"]] as [string, string | null]]
          : []),
      ],
      LoadCharacteristics: [
        ["Customer Class", selectedFilters["Customer Class"]],
        [rateClassLabel, selectedFilters["Rate Class"]],
        // ["Billing Method", selectedFilters["Billing Method"]],
        ...(Array.isArray(adminClients) && adminClients.length > 0 ? [["Pass Through", selectedFilters["Pass Through"]] as [string, string | null]] : []),
        ["Usage", (usage !== undefined && usage !== null) ? String(usage) : null],
        ["Demand", (demand !== undefined && demand !== null) ? String(demand) : null],
        ["Load Factor (%)", selectedFilters["Load Factor"]],
      ],
      PricingParameters: [
        ["Term", selectedFilters["Term"]],
        ["Effective Date", selectedFilters["Effective Date"]],
        ["Product Type", selectedFilters["ProductType"]],
        ["Start Date", selectedFilters["Start Date"]],
        ["Billing Method", selectedFilters["Billing Method"]],
      ],
      // Product: [
      //   ["Product Type", selectedFilters["ProductType"]],
      // ],
    };
    return mapping[sectionKey] || [];
  };

  // const renderBadges = (sectionKey: string) => (
  //   <span className="ms-2 d-inline-flex flex-wrap align-items-center gap-1">
  //     {getBadgesForSection(sectionKey).map(([label, value]) => (
  //       value && value !== 'All' ? (
  //         <Badge key={`${sectionKey}-${label}`} className="mx-1" bg="secondary">
  //           {label}: {value}
  //           <i
  //             className="mdi mdi-close ms-1"
  //             onClick={(e) => handleDeleteBadge(label, e)}
  //             style={{ cursor: 'pointer' }}
  //             role="button"
  //             aria-label={`Remove ${label}`}
  //           />
  //         </Badge>
  //       ) : null
  //     ))}
  //   </span>
  // );
// Renders a consolidated list of all active badges
  const renderAllBadges = () => {
    const sections = [
      "Market",
      "Geography",
      "LoadCharacteristics",
      "PricingParameters",
      "Product",
    ];
    const allBadges = sections.flatMap((sectionKey) =>
      getBadgesForSection(sectionKey)
    );
    const activeBadges = allBadges.filter(
      ([label, value]) => {
        // For Pass Through and Customer Class, show badge even when value is "All"
        if (label === "Pass Through" || label === "Customer Class") {
          return value && value !== "";
        }
        // For all other fields, exclude "All" values
        return value && value !== "All" && value !== "";
      }
    );
    if (activeBadges.length === 0) return null;
    return (
      <Div className="d-flex flex-wrap align-items-center gap-1">
        {activeBadges.map(([label, value]) => (
          <Badge key={`all-${label}`} className="mx-1" bg="secondary">
            {label}: {value}
            <CloseIcon
              className="ms-1"
              onClick={(e) => handleDeleteBadge(label, e)}
              style={{ cursor: "pointer" }}
              role="button"
              aria-label={`Remove ${label}`}
            />
          </Badge>
        ))}
      </Div>
    );
  };
  const handleDeleteBadge = (label: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (typeof onDeleteFilter === 'function') onDeleteFilter(label);
  };

  const getCommodityIcon = (commodity: string) => {
    return commodity === 'NatGas'
      ? <GasIcon className="text-success me-1" size="sm" />
      : <ElectricityIcon className="text-warning me-1" size="sm" />;
  };

  const handleMarketSelect = (market: any) => {
    // Market objects already contain value, name, commodity, and marketIdentifier
    dispatch(setSelectedMarket({
      value: market.value,
      name: market.name,
      commodity: market.commodity
    }));
  };

  // Select first market by default when markets load and none selected
  useEffect(() => {
    if (!selectedMarket && markets && markets.length > 0) {
      // Try to find ERCOT market by name or value (case-insensitive)
      const ercotMarket = markets.find((m: any) => {
        const name = (m.name || m.value || '').toString().toUpperCase();
        return name === 'ERCOT';
      });
      // debugger;
      const marketToSelect = ercotMarket || markets[0];
      const commodity = (marketToSelect as any)?.commodity ?? '';
      dispatch(setSelectedMarket({
        value: marketToSelect.value,
        name: marketToSelect.name,
        commodity
    }));
    }
  }, [markets, selectedMarket, dispatch]);
  return (
    <>
    <Form onSubmit={handleSubmit} className="ranking-filters">
      {/* Market */}
      <Div className="mb-2 mt-0 ranking-section">
        {/* <Div className="row g-3 align-items-center"> */}
        {/* <Div className="row g-3 align-items-start align-items-center align-items-xl-end"> */}
        <Div className="row g-3 align-items-start align-items-lg-center">
          <Div className="col-12 col-md-auto section-heading-column d-flex align-items-start align-items-lg-center">
            <h5>Market</h5>
          </Div>
          <Div className="col">
            <Div className="d-flex flex-wrap gap-1">
              {markets.length === 0 ? (
                <div className="text-muted">No markets available</div>
              ) : (
                markets.map((market: any) => (
                  <Button
                    key={market.id}
                    type="button"
                    variant={
                      selectedMarket?.value === market.value
                        ? "primary"
                        : "outline-secondary"
                    }
                    size="sm"
                   className="rounded-1 d-flex align-items-center gap-1 py-0 px-2"
                    onClick={() => handleMarketSelect(market)}
                  >
                    {getCommodityIcon(market.commodity)}
                    <span className="small">{market.name}</span>
                  </Button>
                ))
              )}
            </Div>
          </Div>
        </Div>
      </Div>

      {/* Geography */}
      {/* <Div className="mb-2">
        <Div className="d-flex align-items-center justify-content-between mb-1">
          <h5 className="mb-0">Geography</h5>
          {renderBadges('Geography')}
        </Div>
        <Div className="d-flex align-items-center gap-2 flex-wrap">
          <FormInput
            type="select"
            label="State:"
            name="stateCode"
            control={control}
            options={dependantDropdowns.states || []}
            errors={errors}
            containerClass={'flex-grow-1'}
            labelClassName="mb-0"
            inline={true}
          />
          <FormInput
            type="select"
            label="Utility:"
            name="utility"
            control={control}
            options={dependantDropdowns.utilities || []}
            errors={errors}
            containerClass={'flex-grow-1'}
            labelClassName="mb-0"
            inline={true}
          />
          {!isGasMarket && (
            <FormInput
              type="select"
              label="Load Zones:"
              name="loadZone"
              control={control}
              options={dependantDropdowns.loadZones || []}
              errors={errors}
              containerClass={'flex-grow-1'}
              labelClassName="mb-0"
              inline={true}
            />
          )}
        </Div>
      </Div> */}

      {/* new code Geography */}
      <Div className="mb-2 geography-section ranking-section">
        <Div className="row g-3 align-items-start align-items-lg-center">
          <Div className="col-12 col-md-auto section-heading-column d-flex align-items-start align-items-lg-center">
            <h5>Geography</h5>
          </Div>
          <Div className="col">
            <Div className="row  align-items-start align-items-xl-end">
              {isGasMarket ? (
                <>
                  <Div className="col-12 col-sm-6 col-lg-3 col-xl-3">
                    <FormInput
                      type="select"
                      label="State"
                      name="stateCode"
                      control={control}
                      options={dependantDropdowns.states || []}
                      errors={errors}
                      labelClassName="form-label geography-label"
                      isLoading={dropdownLoadingState.states}
                      maxMenuHeight={245}
                    />
                  </Div>
                  <Div className="col-12 col-sm-6 col-lg-3 col-xl-3">
                    <FormInput
                      type="select"
                      label="Utility"
                      name="utility"
                      control={control}
                      options={dependantDropdowns.utilities || []}
                      errors={errors}
                      labelClassName="form-label geography-label"
                      isLoading={dropdownLoadingState.utilities}
                      maxMenuHeight={245}
                    />
                  </Div>
                </>
              ) : (
                <>
                  <Div className="col-12 col-sm-6 col-lg-3 col-xl-3">
                    <FormInput
                      type="select"
                      label="State"
                      name="stateCode"
                      control={control}
                      options={dependantDropdowns.states || []}
                      errors={errors}
                      labelClassName="form-label geography-label"
                      isLoading={dropdownLoadingState.states}
                      maxMenuHeight={245}
                    />
                  </Div>
                  <Div className="col-12 col-sm-6 col-lg-3 col-xl-3">
                    <FormInput
                      type="select"
                      label="Utility"
                      name="utility"
                      control={control}
                      options={dependantDropdowns.utilities || []}
                      errors={errors}
                      labelClassName="form-label geography-label"
                      isLoading={dropdownLoadingState.utilities}
                      maxMenuHeight={245}
                    />
                  </Div>
                </>
              )}
              {/* Show Load Zone only for non-gas markets when state is Texas */}
              {!isGasMarket && (
                <Div className="col-12 col-sm-6 col-lg-3 col-xl-3">
                  <FormInput
                    type="select"
                    label="Load Zone"
                    name="loadZone"
                    control={control}
                    options={dependantDropdowns.loadZones || []}
                    errors={errors}
                    labelClassName="form-label geography-label"
                    isLoading={dropdownLoadingState.loadZones}
                    maxMenuHeight={245}
                  />
                </Div>
              )}
            </Div>
          </Div>
        </Div>
      </Div>

      {/* Load Characteristics */}
      {/* <Div className="mb-2">
        <Div className="d-flex align-items-center justify-content-between mb-1">
          <h5 className="mb-0">Load Characteristics</h5>
          {renderBadges('LoadCharacteristics')}
        </Div> */}
        {/* <Div className="d-flex align-items-center gap-2 flex-wrap"> */}
          {/* Show Rate Class for non-Texas states, Load Profile for Texas */}
          {/* {selectedState === 'Texas' ? (
            <FormInput
              type="select"
              label="Load Profile:"
              name="loadProfile"
              control={control}
              options={dependantDropdowns.loadProfiles || []}
              errors={errors}
              containerClass={'flex-grow-1'}
              labelClassName="mb-0"
              inline={true}
            />
          ) : (
            <FormInput
              type="select"
              label="Rate Class:"
              name="rateClass"
              control={control}
              options={dependantDropdowns.rateClasses || []}
              errors={errors}
              containerClass={'flex-grow-1'}
              labelClassName="mb-0"
              inline={true}
            />
          )}
          <FormInput
            type="number"
            label="Usage:"
            name="usage"
            control={control}
            register={register}
            errors={errors}
            placeholder="Enter usage"
            containerClass={'flex-grow-0'}
            labelClassName="mb-0"
            inputClassName="w-auto"
            inline={true}
            min={undefined}
            max={2000000}
            step={1}
            style={{ width: '220px' }}
            validation={{ 
              min: { value: 0, message: 'Usage must be 0 or greater' },
              max: { value: 2000000, message: 'Maximum usage is 2,000,000' },
              setValueAs: (v: any) => {
                // Handle empty/null/undefined as null (not 0)
                if (v === '' || v === null || v === undefined) return null;
                const num = Number(v);
                // Only return valid positive numbers or 0 if explicitly entered
                if (Number.isFinite(num) && num >= 0) return num;
                return null;
              }
            }}
          />
          <FormInput
            type="number"
            label="Demand:"
            name="demand"
            control={control}
            register={register}
            errors={errors}
            placeholder="Enter demand"
            containerClass={'flex-grow-0'}
            labelClassName="mb-0"
            inputClassName="w-auto"
            inline={true}
            min={undefined}
            max={100000}
            step={1}
            style={{ width: '220px' }}
            validation={{ 
              min: { value: 0, message: 'Demand must be 0 or greater' },
              max: { value: 100000, message: 'Maximum demand is 100,000' },
              setValueAs: (v: any) => {
                // Handle empty/null/undefined as null (not 0)
                if (v === '' || v === null || v === undefined) return null;
                const num = Number(v);
                // Only return valid positive numbers or 0 if explicitly entered
                if (Number.isFinite(num) && num >= 0) return num;
                return null;
              }
            }}
          />
        </Div>
      </Div> */}

      {/* Load Characteristics */}
      <Div className="mb-2 load-characteristics-section ranking-section">
        <Div className="row g-3 align-items-start align-items-lg-center">
          <Div className="col-12 col-md-auto section-heading-column d-flex align-items-start align-items-lg-center">
            <h5>Load Characteristics</h5>
          </Div>
          <Div className="col">
            <Div className="row  align-items-start align-items-xl-end">
              <Div className="col-12 col-lg-5 col-xl-5">
                {selectedState === "Texas" ? (
                  <FormInput
                    type="select"
                    label="Load Profile"
                    name="loadProfile"
                    control={control}
                    options={dependantDropdowns.loadProfiles || []}
                    errors={errors}
                    labelClassName="form-label geography-label"
                    isLoading={dropdownLoadingState.loadProfiles}
                    maxMenuHeight={245}
                  />
                ) : (
                  <FormInput
                    type="select"
                    label="Rate Class"
                    name="rateClass"
                    control={control}
                    options={dependantDropdowns.rateClasses || []}
                    errors={errors}
                    labelClassName="form-label geography-label"
                    isLoading={dropdownLoadingState.rateClasses}
                    maxMenuHeight={245}
                  />
                )}
              </Div>
              <Div className="col-12 col-sm-6 col-lg-2 col-xl-2">
                <FormInput
                  type="select"
                  label="Customer Class"
                  name="customerType"
                  control={control}
                  options={dependantDropdowns.customerTypes || []}
                  errors={errors}
                  labelClassName="form-label geography-label text-nowrap"
                  isLoading={dropdownLoadingState.customerTypes}
                  maxMenuHeight={245}
                />
              </Div>
              <Div className="col-12 col-sm-6 col-lg-1 col-xl-1">
                <FormInput
                  type="number"
                  label={demandLabel}
                  name="demand"
                  control={control}
                  register={register}
                  errors={errors}
                  placeholder=""
                  labelClassName="form-label geography-label"
                  inputClassName="w-100 fw-semibold ranking-input-strong"
                  min={undefined}
                  max={100000}
                  step={1}
                  // validation={{
                  //   min: { value: 0, message: "Demand must be 0 or greater" },
                  //   max: { value: 100000, message: "Maximum demand is 100,000" },
                  //   setValueAs: (v: any) => {
                  //     if (v === "" || v === null || v === undefined) return null;
                  //     const num = Number(v);
                  //     if (Number.isFinite(num) && num >= 0) return num;
                  //     return null;
                  //   },
                  // }}
                />
              </Div>

              <Div className="col-12 col-sm-6 col-lg-1 col-xl-1">
                <FormInput
                  type="number"
                  label="Load Factor (%)"
                  name="loadFactor"
                  control={control}
                  register={register}
                  errors={errors}
                  placeholder=""
                  labelClassName="form-label geography-label"
                  inputClassName="w-100 fw-semibold ranking-input-strong"
                  min={undefined}
                  max={2000000}
                  step={1}
                  // validation={{
                  //   min: { value: 0, message: "Load Factor must be 0 or greater" },
                  //   max: { value: 100, message: "Maximum load factor is 2000000" },
                  //   setValueAs: (v: any) => {
                  //     if (v === "" || v === null || v === undefined) return null;
                  //     const num = Number(v);
                  //     if (Number.isFinite(num) && num >= 0) return num;
                  //     return null;
                  //   },
                  // }}
                />
              </Div>

              <Div className="col-12 col-sm-6 col-lg-1 col-xl-1">
                <FormInput
                  type="number"
                  label={usageLabel}
                  name="usage"
                  control={control}
                  register={register}
                  errors={errors}
                  placeholder=""
                  labelClassName="form-label geography-label"
                  inputClassName="w-100 fw-semibold ranking-input-strong"
                  min={undefined}
                  max={2000000}
                  step={1}
                  // validation={{
                  //   min: { value: 0, message: "Usage must be 0 or greater" },
                  //   max: { value: 2000000, message: "Maximum usage is 2,000,000" },
                  //   setValueAs: (v: any) => {
                  //     if (v === "" || v === null || v === undefined) return null;
                  //     const num = Number(v);
                  //     if (Number.isFinite(num) && num >= 0) return num;
                  //     return null;
                  //   },
                  // }}
                />
              </Div>
{Array.isArray(adminClients) && adminClients.length > 0 && (
              <Div className="col-12 col-sm-6 col-lg-2 col-xl-2">
                <FormInput
                  type="select"
                  label="Pass Through"
                  name="passThrough"
                  control={control}
                  options={passThroughOptions}
                  errors={errors}
                  labelClassName="form-label geography-label"
                  multiple
                  placeholder="Select"
                  containerClass="mb-0 w-100"
                  isLoading={dropdownLoadingState.costComponents}
                  maxMenuHeight={245}
                />
              </Div>
               )}

              {/* <Div className="col-12 col-sm-6 col-lg-2 col-xl-2">
                <FormInput
                  type="select"
                  label="Billing Method"
                  name="billingMethod"
                  control={control}
                  options={dependantDropdowns.billingMethods || []}
                  errors={errors}
                  labelClassName="form-label geography-label text-nowrap"
                  isLoading={dropdownLoadingState.billingMethods}
                />
              </Div> */}

              {/* Cost Component */}
              {/* <Div className="col-12 col-sm-6 col-lg-2 col-xl-2">
                <FormInput
                  type="select"
                  label="Cost Component"
                  name="costComponent"
                  control={control}
                  options={costComponentList}
                  errors={errors}
                  labelClassName="form-label geography-label"
                  inputClassName="w-100 fw-semibold ranking-input-strong"
                  min={undefined}
                  max={100000}
                  step={1}
                  validation={{
                    min: { value: 0, message: "Demand must be 0 or greater" },
                    max: { value: 100000, message: "Maximum demand is 100,000" },
                    setValueAs: (v: any) => {
                      if (v === "" || v === null || v === undefined) return null;
                      const num = Number(v);
                      if (Number.isFinite(num) && num >= 0) return num;
                      return null;
                    },
                  }}
                />
              </Div> */}

              {/* <Div className="col-12 col-sm-6 col-lg-2 col-xl-2">
                <FormInput
                  type="number"
                  label="Usage"
                  name="usage"
                  control={control}
                  register={register}
                  errors={errors}
                  placeholder="Enter usage"
                  labelClassName="form-label geography-label"
                  inputClassName="w-100 fw-semibold ranking-input-strong"
                  min={undefined}
                  max={2000000}
                  step={1}
                  validation={{
                    min: { value: 0, message: "Usage must be 0 or greater" },
                    max: { value: 2000000, message: "Maximum usage is 2,000,000" },
                    setValueAs: (v: any) => {
                      if (v === "" || v === null || v === undefined) return null;
                      const num = Number(v);
                      if (Number.isFinite(num) && num >= 0) return num;
                      return null;
                    },
                  }}
                />
              </Div>

              

              {/* Cost Component */}
              {/* <Div className="col-12 col-sm-6 col-lg-2 col-xl-2">
                <FormInput
                  type="select"
                  label="Cost Component"
                  name="costComponent"
                  control={control}
                  options={costComponentList}
                  errors={errors}
                  labelClassName="form-label geography-label"
                  multiple
                  placeholder="All"
                  isLoading={dropdownLoadingState.costComponents}
                />
              </Div> */}

              

              {/* <Div className="col-12 col-sm-6 col-lg-3 col-xl-2">
                <FormInput
                  type="number"
                  label="Demand"
                  name="demand"
                  control={control}
                  register={register}
                  errors={errors}
                  placeholder="Enter demand"
                  labelClassName="form-label geography-label"
                  inputClassName="w-100 fw-semibold ranking-input-strong"
                  min={undefined}
                  max={100000}
                  step={1}
                  validation={{
                    min: { value: 0, message: "Demand must be 0 or greater" },
                    max: { value: 100000, message: "Maximum demand is 100,000" },
                    setValueAs: (v: any) => {
                      if (v === "" || v === null || v === undefined) return null;
                      const num = Number(v);
                      if (Number.isFinite(num) && num >= 0) return num;
                      return null;
                    },
                  }}
                />
              </Div> */}
              {/* <Div className="col-12 col-sm-6 col-lg-2 col-xl-2">
                <FormInput
                  type="checkbox"
                  label="Include Pass Through"
                  name="includePassThrough"
                  register={register}
                  control={control}
                  errors={errors}
                  labelClassName="form-label geography-label"
                />
              </Div> */}
               
            </Div>
          </Div>
        </Div>
      </Div>

      {/* Pricing Parameters */}
      {/* <Div className="mb-2">
        <Div className="d-flex align-items-center justify-content-between mb-1">
          <h5 className="mb-0">Pricing Parameters</h5> */}
          {/* {renderBadges('PricingParameters')} */}
        {/* </Div>
        <Div className="row g-2">
          <FormInput
            type="select"
            label="Term:"
            name="term"
            control={control}
            options={termsList}
            errors={errors}
            containerClass={'col-12 col-md-3'}
            labelClassName="mb-0 me-2"
            inline={true}
          /> */}
          
          {/* Effective Date for Rankings */}
          {/* <Div className="col-12 col-md-6">
            <Div className="d-flex align-items-center gap-2">
              <FormInput
                type="date"
                label="Effective Date:"
                name="effectiveDate"
                key={`effective-${resetKey || ''}`}
                control={control}
                errors={errors}
                placeholder='mm/dd/yy'
                containerClass={'flex-grow-1'}
                labelClassName="mb-0"
                inline={true}
                range
                minDate={new Date(new Date().setFullYear(new Date().getFullYear() - 1))}
                maxDate={new Date()}
              />
            </Div>
          </Div>
        </Div>
      </Div> */}
      <Div className="mb-2 pricing-parameters-section ranking-section">
        <Div className="row g-3 align-items-start align-items-lg-center">
          <Div className="col-12 col-md-auto section-heading-column d-flex align-items-start align-items-lg-center">
            <h5>Pricing Parameters</h5>
          </Div>
          <Div className="col">
            <Div className="row  align-items-start align-items-xl-end">
              <Div className="col-12 col-sm-6 col-lg-3 col-xl-2">
                <FormInput
                  type="select"
                  label="Term"
                  name="term"
                  control={control}
                  options={termsList}
                  errors={errors}
                  labelClassName="form-label geography-label"
                  maxMenuHeight={245}
                />
              </Div>
              <Div className="col-12 col-sm-6 col-lg-3 col-xl-2">
                <FormInput
                  type="date"
                  label="Start Date"
                  name="startToDate"
                  key={`start-date-${resetKey}`}
                  control={control}
                  errors={errors}
                  placeholder="mm/yy"
                  labelClassName="form-label geography-label"
                  onlyMonthPicker
                  highlightToday={false}
                  minDate={new Date(new Date().getFullYear(), new Date().getMonth(), 1)}
                  maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 2))}
                  readOnly={true}
                />
              </Div>
              {/* <Div className="col-12 col-sm-6 col-lg-2 col-xl-2">
                <FormInput
                  type="date"
                  label="Effective Date"
                  name="effectiveDate"
                  key={`effective-${resetKey || ""}`}
                  control={control}
                  errors={errors}
                  placeholder="mm/dd/yy"
                  labelClassName="form-label geography-label"
                  minDate={
                    new Date(new Date().setFullYear(new Date().getFullYear() - 1))
                  }
                  maxDate={new Date()}
                  readOnly={true}
                />
              </Div> */}
              <Div className="col-12 col-sm-6 col-lg-3 col-xl-2">
                <FormInput
                  type="select"
                  label="Product Type"
                  name="productType"
                  control={control}
                  options={productTypeList}
                  errors={errors}
                  labelClassName="form-label geography-label"
                  multiple
                  placeholder="All"
                  maxMenuHeight={245}
                />
              </Div>
              <Div className="col-12 col-sm-6 col-lg-3 col-xl-2">
                <FormInput
                  type="select"
                  label="Billing Method"
                  name="billingMethod"
                  control={control}
                  options={dependantDropdowns.billingMethods || []}
                  errors={errors}
                  labelClassName="form-label geography-label text-nowrap"
                  isLoading={dropdownLoadingState.billingMethods}
                  maxMenuHeight={245}
                />
              </Div>
              <Div className="col-12 col-sm-6 col-lg-3 col-xl-2">
                <FormInput
                  type="select"
                  label="Taxable"
                  name="taxable"
                  control={control}
                  options={BOOLEAN_FILTER_OPTIONS}
                  errors={errors}
                  labelClassName="form-label geography-label"
                  maxMenuHeight={245}
                />
              </Div>
              <Div className="col-12 col-sm-6 col-lg-3 col-xl-2">
                <FormInput
                  type="select"
                  label="POR"
                  name="por"
                  control={control}
                  options={BOOLEAN_FILTER_OPTIONS}
                  errors={errors}
                  labelClassName="form-label geography-label"
                  maxMenuHeight={245}
                />
              </Div>
            </Div>
          </Div>
        </Div>
      </Div>
      {/* <Div className="mt-2 mb-1">{renderAllBadges()}</Div> */}
      {/* <Div className="mt-3 d-flex justify-content-between align-items-center gap-2 flex-wrap">
        {Array.isArray(adminClients) && adminClients.length > 0 ? (
          <Div className="d-flex align-items-center flex-grow-1" style={{ minWidth: 420, maxWidth: 640 }}>
            <FormInput
              type="select"
              label="View as:"
              name="clientOverride"
              control={control}
              options={adminClients}
              errors={errors}
              // containerClass={'flex-grow-1 mb-0'}
              // labelClassName="mb-0 me-2"
              // inline={true}
              labelClassName="form-label geography-label"
              clearable={false}
            />
          </Div>
        ) : <span />} */}

      {/* <Div className="mt-2 mb-1">{renderAllBadges()}</Div> */}

      {/* <Div className="mb-2 pricing-parameters-section ranking-section">
        <Div className="row g-3 align-items-end">
          <Div className="col-12 col-md-auto section-heading-column d-flex align-items-center">
            <h5>View As</h5>
          </Div>
          {Array.isArray(adminClients) && adminClients.length > 0 ? (
            <Div className="col-12 col-md-3">
              <FormInput
                type="select"
                label=""
                name="clientOverride"
                control={control}
                options={adminClients}
                errors={errors}
                labelClassName="form-label geography-label"
                clearable={false}
              />
            </Div>
          ) : (
            <Div className="col"><span /></Div>
          )}
        </Div>
      </Div> */}
      {/* {Array.isArray(adminClients) && adminClients.length > 0 ? (
  <Div className="mb-2 pricing-parameters-section ranking-section">
          <Div className="row g-3 align-items-start align-items-lg-center">
            <Div className="col-12 col-md-auto section-heading-column d-flex align-items-start align-items-lg-center">
              <h5>View As</h5>
            </Div>
            <Div className="col">
              <Div className="row  align-items-start align-items-xl-end">
                <Div className="col-12 col-sm-6 col-lg-3 col-xl-3">
                  <FormInput
                    type="select"
                    label=""
                    name="clientOverride"
                    control={control}
                    options={adminClients}
                    errors={errors}
                    labelClassName="form-label geography-label"
                    clearable={false}
                    isLoading={dropdownLoadingState.adminClients}
                  />
                </Div>
              </Div>
            </Div>
          </Div>
        </Div>
) : (
  <span />
)} */}
<Div className="mt-2 mb-1">{renderAllBadges()}</Div>

      {/* Save Filter Name Modal */}
      <ConfirmationModal
        dialogClassName="modal-md"
        show={showSaveModal}
        onHide={() => setShowSaveModal(false)}
        centered
        size="sm"
        title="Save Filter"
        confirmButtonLabel={savingFilters ? "Saving..." : "Save & Apply"}
        isLoading={savingFilters}
        onConfirm={() => {
          if (filterName.trim()) {
            onSaveFilters?.(filterName.trim());
            setShowSaveModal(false);
            setFilterName("");
          }
        }}
      >
          <FormInput
            type="text"
            name="filterName"
            placeholder="Enter filter name"
            value={filterName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterName(e.target.value)}
            autoFocus
          />
      </ConfirmationModal>
    </Form>
    </>
  );
};

export default AccordionFilters;
